import { useInstantFormat } from './instant-format';
/** Serialized MIME draft saving. Revisions prevent a completed save from marking newer edits as saved. */
import type { DraftInput, DraftSaved } from '@platform/api-client';
import { useI18n } from '@platform/i18n';
import { errorBody, errorCode, toast, useErrorText, type ComposerHandle } from '@platform/ui';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { api } from './api';
import { toApiBody } from './api/real';
import type { ComposeAttachments } from './compose-attachments';
import type { ComposeRequest } from './mail-context';

export function useComposeDrafts(request: ComposeRequest | undefined, composer: RefObject<ComposerHandle | null>, attachments: ComposeAttachments, onSaved: () => void, displayTimeZone?: string | null) {
  const { t } = useI18n();
  const formatDate = useInstantFormat(displayTimeZone);
  const errors = useErrorText();
  const session = useRef(0);
  const revision = useRef(0);
  const savedRevision = useRef(0);
  const stored = useRef<ComposeRequest['draft']>(undefined);
  const inFlight = useRef<Promise<DraftSaved> | undefined>(undefined);
  const sending = useRef(false);
  const [draft, setDraft] = useState<ComposeRequest['draft']>();
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const latest = useRef({ request, attachments, onSaved });
  useEffect(() => { latest.current = { request, attachments, onSaved }; });

  const changed = () => { revision.current += 1; setDirty(true); setFailed(false); };
  const reset = (initial: ComposeRequest['draft']) => {
    session.current += 1;
    revision.current = savedRevision.current = 0;
    stored.current = initial;
    sending.current = false;
    setDraft(initial); setDirty(false); setConflict(false); setConflictOpen(false); setFailed(false);
  };
  const attachmentChange = useRef(attachments.change);
  useEffect(() => {
    if (attachments.change !== attachmentChange.current) { attachmentChange.current = attachments.change; changed(); }
  }, [attachments.change]);

  const input = (): DraftInput => {
    const values = composer.current?.getValues();
    if (!values) throw new Error('Composer unavailable');
    const { request: current, attachments: parts } = latest.current;
    const recipients = (list: typeof values.to) => list.map(value => ({ name: value.name ?? '', email: value.email }));
    return { ...toApiBody(values.body), ...(values.from && { from: values.from }), subject: values.subject,
      to: recipients(values.to), cc: recipients(values.cc), bcc: recipients(values.bcc),
      uploadIds: parts.ids.uploadIds, attachItemIds: parts.ids.attachItemIds, draftAttachmentIds: parts.ids.draftAttachmentIds,
      ...(stored.current && { version: stored.current.version }),
      ...(current?.sourceId && (current.mode === 'reply' || current.mode === 'replyAll' || current.mode === 'forward') && { source: { kind: current.mode === 'forward' ? 'forward' as const : 'reply' as const, itemId: current.sourceId, originalAttachmentIds: parts.ids.originalAttachmentIds } }),
    };
  };

  const save = async (mode: 'normal' | 'new' = 'normal', forSend = false): Promise<DraftSaved> => {
    if (sending.current && !forSend) throw new Error('Draft send in progress');
    while (inFlight.current) await inFlight.current;
    if (sending.current && !forSend) throw new Error('Draft send in progress');
    const active = session.current;
    const current = stored.current;
    const parts = latest.current.attachments;
    if (parts.uploading) { toast.error(t('webmail.compose.uploadsPending')); throw new Error('Uploads pending'); }
    const snapshot = revision.current;
    const sentParts = parts.items.filter(item => !item.error).map(item => ({ ...item }));
    const body = input();
    if (mode === 'new') delete body.version;
    const operation = async () => {
      if (mode === 'new' && current && parts.ids.draftAttachmentIds.length) {
        // Copy preserved file chips through the ordinary checked upload path; the original draft stays intact.
        const uploadIds = [...(body.uploadIds ?? [])];
        for (const item of parts.items.filter(item => item.source === 'draft' && item.ref && !item.error)) {
          const url = api.attachmentDownloadUrl?.(current.id, item.ref!);
          if (!url) throw new Error('Draft attachment unavailable');
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Draft attachment unavailable');
          const blob = await response.blob();
          const uploaded = await api.uploadAttachment(new File([blob], item.name, { type: blob.type }));
          uploadIds.push(uploaded.id);
          const sent = sentParts.find(part => part.id === item.id);
          if (sent) { sent.source = 'upload'; sent.ref = uploaded.id; }
        }
        body.uploadIds = uploadIds; body.draftAttachmentIds = [];
      }
      try { return current && mode !== 'new' ? await api.replaceDraft(current.id, body) : await api.saveDraft(body); }
      catch (error) {
        if (errorCode(error) === 'draft_not_found' && !parts.ids.draftAttachmentIds.length) {
          delete body.version; toast.info(t('webmail.draft.missing')); return api.saveDraft(body);
        }
        throw error;
      }
    };
    setSaving(true); setFailed(false);
    const pending = operation(); inFlight.current = pending;
    try {
      const saved = await pending;
      if (session.current === active) {
        stored.current = saved; setDraft(saved); savedRevision.current = snapshot;
        setDirty(revision.current !== snapshot); setConflict(false); setConflictOpen(false);
        latest.current.attachments.adopt(sentParts, saved.attachments); latest.current.onSaved();
      }
      return saved;
    } catch (error) {
      if (session.current === active) {
        if (errorCode(error) === 'draft_changed') { setConflict(true); setConflictOpen(true); }
        else {
          const part = errorBody<{ error?: { details?: { attachment?: { source?: string; ref?: string; name?: string } } } }>(error)?.error?.details?.attachment;
          const code = errorCode(error);
          const message = code === 'attachment_blocked' ? t('webmail.compose.fileBlocked', { name: part?.name ?? '' }) : code === 'attachment_infected' ? t('webmail.compose.fileInfected', { name: part?.name ?? '' }) : code === 'attachment_scan_unavailable' ? t('webmail.compose.fileScanUnavailable', { name: part?.name ?? '' }) : errors.describe(error);
          if (part?.source && part.ref) latest.current.attachments.refuse(part.source, part.ref, message);
          setFailed(true); toast.error(message);
        }
      }
      throw error;
    } finally { inFlight.current = undefined; setSaving(false); }
  };
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; });
  useEffect(() => {
    if (!request) return;
    let timer: ReturnType<typeof setInterval> | undefined;
    let disposed = false;
    let automatic = false;
    api.attachmentLimits().then(limits => {
      if (disposed) return;
      const seconds = limits.draftAutosaveSeconds ?? 30;
      automatic = seconds > 0;
      if (!automatic) return;
      // Only the first save is automatic. Later edits require an explicit Save as new so no autosave can
      // replace or silently accumulate versions when upstream does not support conditional deletion.
      timer = setInterval(() => { if (!stored.current && !sending.current && revision.current !== savedRevision.current && !inFlight.current && !conflict && !latest.current.attachments.uploading) void saveRef.current().catch(() => undefined); }, seconds * 1000);
    }).catch(errors.notify);
    const visibility = () => { if (automatic && !stored.current && !sending.current && document.visibilityState === 'hidden' && revision.current !== savedRevision.current && !conflict) void saveRef.current().catch(() => undefined); };
    const unload = (event: BeforeUnloadEvent) => { if (revision.current !== savedRevision.current || inFlight.current) { event.preventDefault(); event.returnValue = ''; } };
    document.addEventListener('visibilitychange', visibility); window.addEventListener('beforeunload', unload);
    return () => { disposed = true; clearInterval(timer); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('beforeunload', unload); };
  }, [request, conflict, errors.notify]);
  const discard = async () => {
    await inFlight.current?.catch(() => undefined);
    const current = stored.current;
    if (current) await api.deleteMessage(current.id);
    latest.current.onSaved();
  };
  const prepareSend = async () => {
    sending.current = true;
    try {
      // The first autosave may not have assigned an ID yet. Wait before deciding which send path to use.
      while (inFlight.current) await inFlight.current;
      if (stored.current && revision.current !== savedRevision.current) return await save('new', true);
      return stored.current;
    } catch (error) {
      sending.current = false;
      throw error;
    }
  };
  const showConflict = () => { setConflict(true); setConflictOpen(true); };
  return { draft, dirty, saving, conflict: conflictOpen, changed, reset, save, discard, input, prepareSend, showConflict, resume: () => { sending.current = false; }, cancelConflict: () => setConflictOpen(false),
    status: saving ? t('webmail.draft.saving') : failed ? t('webmail.draft.notSaved') : dirty || !draft ? t('webmail.draft.notSaved') : t('webmail.draft.saved', { time: formatDate(draft.savedAt, { timeStyle: 'short' }) }), statusError: failed };
}
